import type { Layout } from "plotly.js";

const MS_MIN = 60 * 1000;
const MS_HOUR = 60 * 60 * 1000;
const MS_DAY = 24 * MS_HOUR;
const TWO_DAYS_MS = 2 * MS_DAY;

/** Plotly date axes often mutate Date values into strings/numbers in place. */
export function toMs(value: unknown): number | null {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Plotly tickformatstops keyed by dtick (tick spacing), not window length.
 * Sub-minute ticks keep %H:%M:%S so 5m/15m does not collapse to identical HH:MM labels.
 */
export function buildDateTickFormatStops(
  visibleSpanMs: number,
): Pick<Layout["xaxis"], "tickformatstops"> {
  const dayFormat = visibleSpanMs > TWO_DAYS_MS ? "%d %b" : "%a %d %b";
  if (visibleSpanMs > 0 && visibleSpanMs <= 15 * MS_MIN) {
    return { tickformatstops: [{ dtickrange: [null, null], value: "%H:%M:%S" }] };
  }
  return {
    tickformatstops: [
      { dtickrange: [null, MS_MIN], value: "%H:%M:%S" },
      { dtickrange: [MS_MIN, MS_HOUR], value: "%H:%M" },
      { dtickrange: [MS_HOUR, MS_DAY], value: "%a %H:%M" },
      { dtickrange: [MS_DAY, null], value: dayFormat },
    ],
  };
}

/** Tick formats for 7d / all — spacing-aware via Plotly tickformatstops. */
export function buildPowerLongRangeXAxis(
  visibleSpanMs: number,
): Pick<Layout["xaxis"], "tickformatstops"> {
  return buildDateTickFormatStops(visibleSpanMs);
}

export function visibleSpanMsFromX(
  zoomRangeMs: [number, number] | null,
  xRange: [Date, Date] | undefined,
  x: Date[],
): number {
  if (zoomRangeMs) return Math.max(0, zoomRangeMs[1] - zoomRangeMs[0]);
  if (xRange) {
    const from = toMs(xRange[0]);
    const to = toMs(xRange[1]);
    if (from != null && to != null) return Math.max(0, to - from);
  }
  if (x.length >= 2) {
    const from = toMs(x[0]);
    const to = toMs(x[x.length - 1]);
    if (from != null && to != null) return Math.max(0, to - from);
  }
  return 7 * MS_DAY;
}

/** Fresh ms pair for Plotly so it cannot mutate our Date state. */
export function plotlyDateRangeMs(
  xRange: [Date, Date] | undefined,
): [number, number] | undefined {
  if (!xRange) return undefined;
  const from = toMs(xRange[0]);
  const to = toMs(xRange[1]);
  if (from == null || to == null) return undefined;
  return [from, to];
}

export function parsePlotlyXRange(relayout: Record<string, unknown>): [number, number] | "autorange" | null {
  if (relayout["xaxis.autorange"] === true) return "autorange";

  const r0 = relayout["xaxis.range[0]"];
  const r1 = relayout["xaxis.range[1]"];
  if (r0 != null && r1 != null) {
    const from = toMs(r0);
    const to = toMs(r1);
    if (from != null && to != null) return [from, to];
  }

  const range = relayout["xaxis.range"];
  if (Array.isArray(range) && range.length === 2) {
    const from = toMs(range[0]);
    const to = toMs(range[1]);
    if (from != null && to != null) return [from, to];
  }

  return null;
}
